import { Box } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { Sidebar } from "../components";
import { GraphContainer } from "../components/Graph/GraphContainer";
import { FacilityOwnerFilterData } from "../components/Layout/filters/FacilityOwnerFilter";
import { ProjectOrganizationFilterData } from "../components/Layout/filters/ProjectOrganizationFilter";
import { RelationshipFilterData } from "../components/Layout/filters/RelationshipFilter";
import { GraphParameters } from "../components/Layout/filters/sections/GraphSettingsSection";
import { CompanyOption } from "../components/Layout/filters/VirtualizedCompanyDropdown";
import SearchFilterPanel from "../components/Search/SearchFilterPanel";
import { getOrganizationSuggestions } from "../services/searchApi";
import { getDbValuesFromDisplayNames as getFacilityStatusDbValues } from "../utils/facilityStatusMapping";
import { getDbValuesFromDisplayNames } from "../utils/facilityTypeMapping";
import {
  getDbValuesFromDisplayNames as getOrgCategoryDbValues,
  getDisplayNamesFromDbValues as getOrgCategoryDisplayNames,
} from "../utils/organizationCategoryMapping";
import { regionMapping } from "../utils/regionMapping";
import { updateFilterInURL } from "../utils/urlFilterHelpers";

interface SearchFilters {
  searchTerm: string;
  entityType: "organizations" | "facilities" | "projects" | "sources";
  regions: string[];
  industries: string[];
  facilityTypes: string[];
  organizationTypes: string[];
  projectTypes: string[];
  sourceTypes: string[];
  labels: string[];
  dateRange: {
    start?: string;
    end?: string;
  };
  customFilters: Record<string, any>;
  relationshipFilters: RelationshipFilterData[];
  facilityOwnerFilter: FacilityOwnerFilterData;
  projectOrganizationFilter: ProjectOrganizationFilterData;
}

const GraphView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef<any>();
  const refreshTriggerRef = useRef<(() => void) | null>(null);

  // Track last refreshed parameters to detect changes
  // Store all URL params except UI-only ones (detail overlays)
  const [lastRefreshedParams, setLastRefreshedParams] = useState<string | null>(
    null
  );

  // Initialize graph parameters from URL (excluding display settings)
  const [graphParameters, setGraphParameters] = useState<GraphParameters>({
    entityId: searchParams.get("seed_entity") || "",
    selectedCompany: null,
    orgStructure: searchParams.get("org_structure") || "",
    supplierTiers: parseInt(searchParams.get("supplier_tiers") || "0"),
    customerTiers: parseInt(searchParams.get("customer_tiers") || "0"),
    // Display settings are pure frontend state, not URL-synced
    layoutType: "hierarchy",
    showEdgeLabels: true,
    showNodeLabels: true,
  });

  // Derive filters from URL parameters for SearchFilterPanel
  const filters = useMemo<SearchFilters>(
    () => ({
      searchTerm: searchParams.get("search") || "",
      entityType: "organizations", // Graph view is always organizations
      regions: searchParams.get("regions")
        ? searchParams.get("regions")!.split(",")
        : [],
      industries: searchParams.get("industries")
        ? searchParams.get("industries")!.split(",")
        : [],
      facilityTypes: [],
      organizationTypes: (() => {
        const orgTypesParam = searchParams.get("organizationTypes");
        if (!orgTypesParam) return [];
        const typeValues = orgTypesParam.split(",");
        try {
          return getOrgCategoryDisplayNames(typeValues);
        } catch {
          return typeValues;
        }
      })(),
      projectTypes: [],
      sourceTypes: [],
      labels: searchParams.get("labels")
        ? searchParams.get("labels")!.split(",")
        : [],
      dateRange: {
        start: searchParams.get("dateStart") || undefined,
        end: searchParams.get("dateEnd") || undefined,
      },
      customFilters: {
        naicsCodes: searchParams.get("naics")
          ? searchParams.get("naics")!.split(",")
          : [],
        inputHSCodes: searchParams.get("hsInput")
          ? searchParams.get("hsInput")!.split(",")
          : [],
        outputHSCodes: searchParams.get("hsOutput")
          ? searchParams.get("hsOutput")!.split(",")
          : [],
        homeCountries: searchParams.get("homeCountries")
          ? searchParams.get("homeCountries")!.split(",")
          : [],
        foundingYearMin: searchParams.get("foundingYearMin") || undefined,
        foundingYearMax: searchParams.get("foundingYearMax") || undefined,
      },
      relationshipFilters: (() => {
        const relationshipParam = searchParams.get("relationships");
        if (!relationshipParam) return [];
        try {
          return JSON.parse(decodeURIComponent(relationshipParam));
        } catch {
          return [];
        }
      })(),
      facilityOwnerFilter: { ownerCompanies: [], maxTiers: 1 },
      projectOrganizationFilter: { participantCompanies: [], maxTiers: 1 },
    }),
    [searchParams]
  );

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    const filterCount =
      (filters.regions?.length || 0) +
      (filters.organizationTypes?.length || 0) +
      (filters.labels?.length || 0) +
      (filters.customFilters.naicsCodes?.length || 0) +
      (filters.customFilters.inputHSCodes?.length || 0) +
      (filters.customFilters.outputHSCodes?.length || 0) +
      (filters.customFilters.homeCountries?.length || 0) +
      (filters.customFilters.foundingYearMin ? 1 : 0) +
      (filters.customFilters.foundingYearMax ? 1 : 0) +
      (filters.relationshipFilters?.length || 0) +
      (filters.dateRange.start ? 1 : 0) +
      (filters.dateRange.end ? 1 : 0);

    // Graph parameters are now counted by GraphContainer
    const graphParamCount =
      (searchParams.get("seed_entity") ? 1 : 0) +
      (searchParams.get("org_structure") ? 1 : 0) +
      (parseInt(searchParams.get("supplier_tiers") || "0") > 0 ? 1 : 0) +
      (parseInt(searchParams.get("customer_tiers") || "0") > 0 ? 1 : 0);

    return filterCount + graphParamCount;
  }, [filters, searchParams]);

  // Sync graph parameters with URL changes (including when cleared)
  useEffect(() => {
    const urlEntityId = searchParams.get("seed_entity") || "";
    const urlOrgStructure = searchParams.get("org_structure") || "";
    const urlSupplierTiers = parseInt(
      searchParams.get("supplier_tiers") || "0"
    );
    const urlCustomerTiers = parseInt(
      searchParams.get("customer_tiers") || "0"
    );

    // Update graph parameters if URL values differ from current state
    setGraphParameters((prev) => {
      const needsUpdate =
        prev.entityId !== urlEntityId ||
        prev.orgStructure !== urlOrgStructure ||
        prev.supplierTiers !== urlSupplierTiers ||
        prev.customerTiers !== urlCustomerTiers;

      if (needsUpdate) {
        return {
          ...prev,
          entityId: urlEntityId,
          orgStructure: urlOrgStructure,
          supplierTiers: urlSupplierTiers,
          customerTiers: urlCustomerTiers,
          // Clear selectedCompany if entityId is cleared
          selectedCompany: urlEntityId ? prev.selectedCompany : null,
        };
      }
      return prev;
    });
  }, [searchParams]);

  // Eager loading: Fetch company data when seed_entity is in URL
  useEffect(() => {
    const loadInitialCompanyData = async () => {
      const urlEntityId = searchParams.get("seed_entity");

      // Only load if we have a URL entity ID and either no company data or ID mismatch
      if (
        urlEntityId &&
        (!graphParameters.selectedCompany ||
          graphParameters.selectedCompany.id !== urlEntityId)
      ) {
        try {
          const response = await getOrganizationSuggestions(urlEntityId, 1);
          if (response.suggestions.length > 0) {
            const org = response.suggestions[0];
            const companyOption: CompanyOption = {
              id: org.id,
              name: org.name,
              country: org.country,
              country_iso2: org.country_iso2,
              display_label: org.display_label,
            };
            setGraphParameters((prev) => ({
              ...prev,
              entityId: urlEntityId,
              selectedCompany: companyOption,
            }));
          }
        } catch (error) {
          console.error("Failed to load initial company data:", error);
        }
      }
    };

    loadInitialCompanyData();
  }, [searchParams]); // Re-run when URL changes

  // Sync selectedCompany when entityId changes (after initial load)
  useEffect(() => {
    const syncSelectedCompany = async () => {
      if (
        graphParameters.entityId &&
        (!graphParameters.selectedCompany ||
          graphParameters.selectedCompany.id !== graphParameters.entityId)
      ) {
        try {
          const response = await getOrganizationSuggestions(
            graphParameters.entityId,
            1
          );
          if (response.suggestions.length > 0) {
            const org = response.suggestions[0];
            const companyOption: CompanyOption = {
              id: org.id,
              name: org.name,
              country: org.country,
              country_iso2: org.country_iso2,
              display_label: org.display_label,
            };
            setGraphParameters((prev) => ({
              ...prev,
              selectedCompany: companyOption,
            }));
          }
        } catch (error) {
          // Silently fail
        }
      }
    };

    syncSelectedCompany();
  }, [graphParameters.entityId, graphParameters.selectedCompany]);

  // Helper function to get relevant URL params (excluding UI-only params)
  const getRelevantParams = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    // Remove UI-only parameters that don't affect graph data
    params.delete("detail_entity_id");
    params.delete("detail_relationship_id");
    // Sort params for consistent comparison
    params.sort();
    return params.toString();
  }, [searchParams]);

  // Check if data parameters have changed since last refresh
  const hasParameterChanges = useMemo(() => {
    if (!graphParameters.entityId) return false; // No entity selected
    if (!lastRefreshedParams) return true; // No refresh yet, enable button

    const currentParams = getRelevantParams();
    return currentParams !== lastRefreshedParams;
  }, [graphParameters.entityId, lastRefreshedParams, getRelevantParams]);

  const handleGraphParameterChange = useCallback(
    (key: keyof GraphParameters, value: any) => {
      // Update local state
      setGraphParameters((prev) => ({ ...prev, [key]: value }));

      // Sync to URL (only for data parameters, not display settings)
      if (
        key === "entityId" ||
        key === "orgStructure" ||
        key === "supplierTiers" ||
        key === "customerTiers"
      ) {
        setSearchParams(
          (prev) => {
            const params = new URLSearchParams(prev);
            const urlKey =
              key === "entityId"
                ? "seed_entity"
                : key === "orgStructure"
                  ? "org_structure"
                  : key === "supplierTiers"
                    ? "supplier_tiers"
                    : "customer_tiers";

            if (value && (typeof value !== "number" || value > 0)) {
              params.set(urlKey, value.toString());
            } else {
              params.delete(urlKey);
            }
            return params;
          },
          { replace: true }
        );
      }
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback(
    (filterKey: keyof SearchFilters, value: any) => {
      updateFilterInURL(filterKey, value, searchParams, setSearchParams, {
        getDbValuesFromDisplayNames,
        getFacilityStatusDbValues,
        getOrgCategoryDbValues,
      });
    },
    [searchParams, setSearchParams]
  );

  const handleClearAll = useCallback(() => {
    // Clear all URL parameters
    const params = new URLSearchParams();
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  return (
    <Box sx={{ display: "flex", height: "100%", backgroundColor: "#262626" }}>
      {/* Sidebar */}
      <Sidebar />

      {/* Filter Panel */}
      <SearchFilterPanel
        viewMode="graph"
        filters={filters}
        onFilterChange={handleFilterChange}
        activeFilterCount={activeFilterCount}
        graphParameters={graphParameters}
        onGraphParameterChange={handleGraphParameterChange}
        onGraphRefresh={() => {
          if (refreshTriggerRef.current) {
            refreshTriggerRef.current();
          }
        }}
        hasParameterChanges={hasParameterChanges}
        graphLoading={loading}
        onClearAll={handleClearAll}
      />

      {/* Main Graph View */}
      <Box sx={{ flex: 1, position: "relative" }}>
        <GraphContainer
          graphParameters={graphParameters}
          onLoadingChange={setLoading}
          onGraphDataLoaded={(fetchedEntityId) => {
            // Construct params string with the entityId that was actually fetched
            // This avoids race conditions with searchParams state updates
            const params = new URLSearchParams(searchParams);
            params.set("seed_entity", fetchedEntityId);
            params.delete("detail_entity_id");
            params.delete("detail_relationship_id");
            params.sort();
            setLastRefreshedParams(params.toString());
          }}
          canvasRef={canvasRef}
          refreshTriggerRef={refreshTriggerRef}
          filters={{
            homeCountries: (() => {
              if (!filters.customFilters.homeCountries?.length) return [];
              return regionMapping.getCountryCodesFromIds(
                filters.customFilters.homeCountries
              );
            })(),
            organizationTypes: (() => {
              try {
                return getOrgCategoryDbValues(filters.organizationTypes);
              } catch {
                return filters.organizationTypes;
              }
            })(),
            naicsCodes: filters.customFilters.naicsCodes,
            inputHSCodes: filters.customFilters.inputHSCodes,
            outputHSCodes: filters.customFilters.outputHSCodes,
            foundingYearMin: filters.customFilters.foundingYearMin,
            foundingYearMax: filters.customFilters.foundingYearMax,
            labels: filters.labels,
            relationshipFilters: filters.relationshipFilters,
          }}
        />
      </Box>
    </Box>
  );
};

export default GraphView;
